import { useQuery } from '@tanstack/react-query';
import { queueService } from '@/services/queue-service';

// Statuses that indicate active/pending operations requiring frequent polling
const ACTIVE_STATUSES = ['Processing', 'Pending', 'Blocked'] as const;

/**
 * Hook for fetching queue data with smart polling
 * - Polls every 5s when there are active operations (Processing/Pending/Blocked)
 * - Polls every 30s when idle (no active operations)
 * - Stops polling when disabled
 */
export function useQueue(companyId: number | undefined, enabled: boolean, allCompanies = false) {
  const query = useQuery({
    queryKey: ['queue', companyId, allCompanies],
    queryFn: () => queueService.getQueues(companyId!, allCompanies),
    enabled: !!companyId && enabled,
    // Dynamic refetch interval based on queue state
    refetchInterval: (query) => {
      if (!enabled) return false;
      
      const data = query.state.data;
      if (!data || !Array.isArray(data)) return 30000; // 30s default when no data
      
      // Check if there are any active operations
      const hasActiveOperations = data.some(
        (item: { Status?: string }) => 
          item.Status && ACTIVE_STATUSES.includes(item.Status as typeof ACTIVE_STATUSES[number])
      );
      
      // Poll frequently (5s) when active, slowly (30s) when idle
      return hasActiveOperations ? 5000 : 30000;
    },
    // Add error handling to prevent infinite retry loops
    retry: (failureCount, error) => {
      // Prefer structured error information over string matching
      const anyError = error as { response?: { status?: number }; status?: number };
      const status = anyError?.response?.status ?? anyError?.status;

      const isAuthStatusError = status === 401 || status === 403;

      // Fallback to message inspection only if no status is available
      const isAuthMessageError =
        !isAuthStatusError &&
        error instanceof Error &&
        (
          error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized')
        );

      // JSON parse errors indicate HTML login page was returned instead of JSON
      const isParseError = error instanceof SyntaxError;

      const isAuthError = isAuthStatusError || isAuthMessageError || isParseError;
      if (isAuthError) {
        return false;
      }
      // Allow up to 3 retries for other errors
      return failureCount < 3;
    },
    // Reduce retry delay for better UX
    retryDelay: 1000,
  });

  return query;
}
